import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ShoppingBag, Share2, Milk } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/lib/content";

const PLACEHOLDER = "https://images.unsplash.com/photo-1631014304559-24a497774470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxuYXR1cmFsJTIwbWVkaWNpbmUlMjBib3R0bGUlMjBwbGFudHxlbnwwfHx8fDE3ODIxMTI1NjF8MA&ixlib=rb-4.1.0&q=85";
const CATS = ["all", "yoga-essentials", "gir-cow", "natural", "tonics", "churna", "oils", "tablets", "supplements", "general"];
const CAT_LABELS = {
  all: "All",
  "yoga-essentials": "Yoga Essentials",
  "gir-cow": "Gir Cow · ગવ્ય",
};

export default function Shop() {
  const { c: txt } = useContent();
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState(CATS.includes(params.get("category")) ? params.get("category") : "all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/products", { params: { category: cat, search: q } }).then(({ data }) => setProducts(data)).catch(() => {});
  }, [cat, q]);

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="overline mb-2" data-testid="shop-overline">{txt("shop.hero.overline")}</div>
          <h1 className="font-display text-4xl lg:text-5xl" data-testid="shop-title">{txt("shop.hero.title")}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{txt("shop.hero.subtitle")}</p>
        </div>
        <div className="relative w-full lg:w-80">
          <Search className="absolute left-3 top-3.5 text-muted-foreground" size={16}/>
          <Input data-testid="shop-search" placeholder="Search formulations…" className="pl-9 bg-transparent h-11"
            value={q} onChange={(e)=>setQ(e.target.value)} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
            className={`px-4 py-2 rounded-full text-sm capitalize transition-all ${
              cat === c ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary/40"}`}>
            {CAT_LABELS[c] || c.replace("-", " ")}
          </button>
        ))}
      </div>

      {cat === "gir-cow" && (
        <div
          className="rounded-2xl border border-secondary/40 bg-secondary/5 px-5 py-4 mb-8 flex flex-wrap items-start gap-3"
          data-testid="gavya-banner"
        >
          <span className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center shrink-0">
            <Milk size={16} />
          </span>
          <div>
            <div className="font-display text-xl" data-testid="gavya-banner-title">{txt("shop.gavya.title")}</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">{txt("shop.gavya.body")}</p>
          </div>
        </div>
      )}

      {products.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl">
          <ShoppingBag className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No products listed yet. The Vaidya is curating the marketplace.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {products.map((p) => (
            <Link to={`/shop/${p.id}`} key={p.id} className="product-card group relative border border-transparent hover:border-border rounded-2xl p-3 transition-all" data-testid={`product-${p.id}`}>
              <button
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  const url = `${window.location.origin}/shop/${p.id}`;
                  if (navigator.share) {
                    navigator.share({ title: p.name, text: `${p.name} — Immortal Vedas`, url }).catch(()=>{});
                  } else {
                    navigator.clipboard?.writeText(url);
                    toast.success("Buy link copied");
                  }
                }}
                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-background/90 backdrop-blur border border-border flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground"
                title="Share buy link"
                aria-label="Share buy link"
                data-testid={`shop-share-${p.id}`}
              >
                <Share2 size={13}/>
              </button>
              <div className="aspect-square overflow-hidden rounded-xl bg-muted mb-4">
                <img
                  src={mediaUrl(p.image) || PLACEHOLDER}
                  alt={p.name}
                  onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                  className="w-full h-full object-cover product-img-zoom"
                  data-testid={`shop-img-${p.id}`}
                />
              </div>
              <div className="px-1">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-secondary/30 text-secondary text-[10px] capitalize">{CAT_LABELS[p.category] || p.category.replace("-", " ")}</Badge>
                  {p.stock <= 0 && <span className="text-[10px] text-destructive">Out of stock</span>}
                </div>
                <h3 className="font-display text-xl mt-2 group-hover:text-primary transition-colors">{p.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-medium text-primary">₹{p.price?.toFixed(2)}</span>
                  <span className="text-xs text-secondary underline">View →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
